import { Fragment, useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Autocomplete,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  IconButton,
  LinearProgress,
  MenuItem,
  Popover,
  Stack,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
  Alert,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import SettingsIcon from '@mui/icons-material/Settings';
import ScheduleIcon from '@mui/icons-material/Schedule';
import EditNoteIcon from '@mui/icons-material/EditNote';
import TuneIcon from '@mui/icons-material/Tune';
import BarChartIcon from '@mui/icons-material/BarChart';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import { useBrand } from '../contexts/BrandContext';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';
import { auditsApi, type AuditProgress } from '../services/audits';
import { formatDate, formatDateTime } from '../i18n/format';
import { bareHost, sourceHref, isGroundingWrapper } from '../utils/sourceUrl';
import { brandQueriesApi } from '../services/brand-queries';
import { proposedArticlesApi } from '../services/proposed-articles';
// QueryManager replaced by PromptStudioDialog (lot B step 4); kept on disk until step 9
// import QueryManager from '../components/brand/QueryManager';
import PromptStudioDialog from '../components/prompt-studio/PromptStudioDialog';
import { isGscCallbackResult } from '../components/prompt-studio/searchConsoleModel';
import { setPendingGscReturn } from '../components/prompt-studio/searchConsoleStore';
import LaunchDialog from '../components/prompt-studio/LaunchDialog';
import BrandAxisDashboard from '../components/brand/BrandAxisDashboard';
import TimeframeSelector from '../components/common/TimeframeSelector';
import SegmentedControl from '../components/common/SegmentedControl';
// One copy of the inclusive-span / 90-day rule, shared with BrandAxisDashboard.
import { BP_MAX_SPAN_DAYS, resolveBpRange, useBpTimeframe } from '../components/brand/bpRange';
import { cellSources, columnLabel, isScored, kindBreaks, lastScore } from '../components/brand/promptMatrixCells';
import { AiOverviewLogo, MistralLogo } from '../components/brand/EngineLogo';
import { RecommendationList } from '../components/recommendations/RecommendationList';
import { useTranslation } from 'react-i18next';
import { StrategySuggestDialog } from '../components/recommendations/StrategySuggestDialog';
import { api } from '../services/api';
import { fromBrandAxis, fromStringList, BRAND_STRATEGY_LIMITS, isBrandOwnedHost, storedPlaceKey, languageKeyOf, promptIdentityKey } from '@traaker/shared';
import type {
  Audit,
  AuditType,
  BPPromptFact,
  BPPromptMatrixResponse,
  UnifiedRecommendation,
  BrandAxisAuditType,
  BrandAxisRecommendation,
  BrandNarrativeSummary,
  BrandAuthoritySummary,
  BrandReferralSummary,
  BrandQuery,
  BrandQueryAxis,
} from '@traaker/shared';
import { CATEGORY_CHIP_COLOR, sourceColorsFrom } from '../utils/targetColors';
// The answer renderer lives in components/common so the Conversations transcript
// renders an engine answer exactly the way this page does — one copy.
import {
  extractDomain,
  HighlightedResponse,
  useMarqueColors,
} from '../components/common/HighlightedResponse';

type AxisAgent = 'brand_narrative' | 'brand_authority' | 'brand_referral' | 'brand_comparison';

const AXIS_META: Record<BrandQueryAxis, {
  /** i18n keys — resolved with `t()` at render. */
  titleKey: string;
  subtitleKey: string;
  auditType: AuditType;
  agentType: AxisAgent;
  color: string;
}> = {
  narrative: {
    titleKey: 'axisPage.narrative.title',
    subtitleKey: 'axisPage.narrative.subtitle',
    auditType: 'brand_narrative',
    agentType: 'brand_narrative',
    color: '#8B5CF6',
  },
  authority: {
    titleKey: 'axisPage.authority.title',
    subtitleKey: 'axisPage.authority.subtitle',
    auditType: 'brand_authority',
    agentType: 'brand_authority',
    color: '#00DDB3',
  },
  referral: {
    titleKey: 'axisPage.referral.title',
    subtitleKey: 'axisPage.referral.subtitle',
    auditType: 'brand_referral',
    agentType: 'brand_referral',
    color: '#FF6B35',
  },
  // Comparaison axis (Prompt Studio, 2026-10-15). Surfaces added by lot F.
  comparison: {
    titleKey: 'axisPage.comparison.title',
    subtitleKey: 'axisPage.comparison.subtitle',
    auditType: 'brand_comparison',
    agentType: 'brand_comparison',
    color: '#1d4ed8',
  },
};


const ALIGNMENT_COLORS: Record<string, string> = {
  strong: '#00DDB3',
  partial: '#FF6B35',
  weak: '#FF4444',
  misaligned: '#FF4444',
};

/** i18n keys — resolved with `t()` at render. */
const ALIGNMENT_LABEL_KEYS: Record<string, string> = {
  strong: 'axisPage.alignment.strong',
  partial: 'axisPage.alignment.partial',
  weak: 'axisPage.alignment.weak',
  misaligned: 'axisPage.alignment.misaligned',
};

function isBrandUrl(url: string, brandName?: string, brandUrl?: string) {
  if (!brandName && !brandUrl) return false;
  const ref = bareHost(brandUrl || brandName);
  if (!ref) return false;
  // Compare bare hostnames so `vitalis-poitiers.fr` source matches a brand
  // record stored as `https://www.vitalis-poitiers.fr/`. Ownership (apex,
  // subdomain, sibling TLD) is `isBrandOwnedHost` — the same rule the BP agent's
  // `scoring.is_brand_host` uses to compute the persisted `brandIsSource`, so the
  // green chip can never contradict the "Votre site est source" badge next to it.
  const host = bareHost(url);
  if (host) return isBrandOwnedHost(host, ref);
  // Fallback to string-include for free-text brand names that don't parse
  // as URLs (e.g. brandUrl is empty and we got the brand name as ref).
  return url.toLowerCase().includes(ref);
}

type EngineKey =
  | 'perplexity'
  | 'openai'
  | 'gemini'
  | 'claude'
  | 'mistral'
  | 'ai_overview'
  | 'google_ai_mode'
  | 'unknown';

// `secondary: true` = a simulated engine (a model + a web-search harness we own),
// shown with an "indicatif" chip. It approximates a product, it isn't the product.
const ENGINE_META: Record<EngineKey, { label: string; color: string; bg: string; secondary?: boolean }> = {
  perplexity: { label: 'Perplexity', color: '#20B8CD', bg: '#20B8CD22' },
  openai: { label: 'ChatGPT', color: '#10A37F', bg: '#10A37F22' },
  gemini: { label: 'Gemini', color: '#4285F4', bg: '#4285F422' },
  claude: { label: 'Claude', color: '#D97757', bg: '#D9775722' },
  mistral: { label: 'Mistral', color: '#FF7000', bg: '#FF700022', secondary: true },
  // Google red, not Google blue — Gemini already owns #4285F4 and the two would
  // be indistinguishable as chart series.
  ai_overview: { label: 'AI Overview', color: '#EA4335', bg: '#EA433522', secondary: true },
  // Purple — distinct from Gemini blue (#4285F4), AI Overview red (#EA4335).
  google_ai_mode: { label: 'Google AI Mode', color: '#9334E6', bg: '#9334E622', secondary: true },
  unknown: { label: 'Moteur', color: '#8B9DC3', bg: '#8B9DC322' },
};

const KNOWN_ENGINE_KEYS = new Set<EngineKey>([
  'perplexity',
  'openai',
  'gemini',
  'claude',
  'mistral',
  'ai_overview',
  'google_ai_mode',
]);

function engineKey(e?: string): EngineKey {
  const k = (e || '').toLowerCase() as EngineKey;
  return KNOWN_ENGINE_KEYS.has(k) ? k : 'unknown';
}

function EngineLogo({ engine, size = 20 }: { engine?: string; size?: number }) {
  const k = engineKey(engine);
  const meta = ENGINE_META[k];
  const common = {
    width: size,
    height: size,
    viewBox: '0 0 24 24',
    style: { display: 'block' as const },
  };
  if (k === 'perplexity') {
    return (
      <svg {...common} fill={meta.color} aria-label="Perplexity">
        <path d="M12 2.5 3.5 7.5v9L12 21.5l8.5-5v-9L12 2.5Zm0 2.31 6.5 3.82v7.74L12 19.19l-6.5-3.82V8.63L12 4.81Z" />
        <path d="M7.5 9.5h9v1.5h-9zM7.5 12h9v1.5h-9z" />
      </svg>
    );
  }
  if (k === 'openai') {
    return (
      <svg {...common} fill={meta.color} aria-label="ChatGPT">
        <path d="M22.28 9.82a5.98 5.98 0 0 0-.52-4.91 6.05 6.05 0 0 0-6.52-2.9A6 6 0 0 0 5.07 4.16a5.98 5.98 0 0 0-4 2.91 6.05 6.05 0 0 0 .75 7.11 5.98 5.98 0 0 0 .52 4.91 6.05 6.05 0 0 0 6.52 2.9A6 6 0 0 0 18.93 19.84a5.98 5.98 0 0 0 4-2.91 6.05 6.05 0 0 0-.65-7.11ZM13.19 21.1a4.49 4.49 0 0 1-2.88-1.04l.14-.08 4.77-2.76a.78.78 0 0 0 .39-.68v-6.75L17.63 11v5.57a4.5 4.5 0 0 1-4.44 4.53Zm-9.66-4.12a4.48 4.48 0 0 1-.54-3.03l.14.08 4.78 2.76a.77.77 0 0 0 .78 0l5.84-3.37v2.33L10.7 18.5a4.5 4.5 0 0 1-6.17-1.52ZM2.56 8.66a4.49 4.49 0 0 1 2.34-1.97v5.68a.77.77 0 0 0 .39.67l5.81 3.35-2.02 1.16a.07.07 0 0 1-.07 0l-4.83-2.79a4.5 4.5 0 0 1-1.62-6.1Zm16.57 3.86-5.83-3.4 2.02-1.16a.07.07 0 0 1 .07 0l4.82 2.79a4.5 4.5 0 0 1-.67 8.1v-5.68a.78.78 0 0 0-.41-.65Zm2.02-3.03-.14-.09L16.23 6.6a.77.77 0 0 0-.79 0L9.62 9.97V7.64l4.83-2.79a4.5 4.5 0 0 1 6.7 4.64ZM8.52 13.57 6.5 12.4V6.84a4.5 4.5 0 0 1 7.38-3.46l-.14.08L8.97 6.22a.78.78 0 0 0-.39.67Zm1.1-2.37L12.22 9.67l2.62 1.5v3.01l-2.6 1.51-2.62-1.5Z" />
      </svg>
    );
  }
  if (k === 'gemini') {
    return (
      <svg {...common} viewBox="0 0 24 24" aria-label="Gemini">
        <defs>
          <linearGradient id="gm" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="#4285F4" />
            <stop offset="50%" stopColor="#9B72F2" />
            <stop offset="100%" stopColor="#D96570" />
          </linearGradient>
        </defs>
        <path
          fill="url(#gm)"
          d="M12 2c.4 4.6 2.4 6.6 7 7-4.6.4-6.6 2.4-7 7-.4-4.6-2.4-6.6-7-7 4.6-.4 6.6-2.4 7-7Z"
        />
      </svg>
    );
  }
  if (k === 'claude') {
    return (
      <svg
        width={size}
        height={size}
        viewBox="0 0 100 100"
        fill={meta.color}
        aria-label="Claude"
        style={{ display: 'block' }}
      >
        <path d="m19.6 66.5 19.7-11 .3-1-.3-.5h-1l-3.3-.2-11.2-.3L14 53l-9.5-.5-2.4-.5L0 49l.2-1.5 2-1.3 2.9.2 6.3.5 9.5.6 6.9.4L38 49.1h1.6l.2-.7-.5-.4-.4-.4L29 41l-10.6-7-5.6-4.1-3-2-1.5-2-.6-4.2 2.7-3 3.7.3.9.2 3.7 2.9 8 6.1L37 36l1.5 1.2.6-.4.1-.3-.7-1.1L33 25l-6-10.4-2.7-4.3-.7-2.6c-.3-1-.4-2-.4-3l3-4.2L28 0l4.2.6L33.8 2l2.6 6 4.1 9.3L47 29.9l2 3.8 1 3.4.3 1h.7v-.5l.5-7.2 1-8.7 1-11.2.3-3.2 1.6-3.8 3-2L61 2.6l2 2.9-.3 1.8-1.1 7.7L59 27.1l-1.5 8.2h.9l1-1.1 4.1-5.4 6.9-8.6 3-3.5L77 13l2.3-1.8h4.3l3.1 4.7-1.4 4.9-4.4 5.6-3.7 4.7-5.3 7.1-3.2 5.7.3.4h.7l12-2.6 6.4-1.1 7.6-1.3 3.5 1.6.4 1.6-1.4 3.4-8.2 2-9.6 2-14.3 3.3-.2.1.2.3 6.4.6 2.8.2h6.8l12.6 1 3.3 2 1.9 2.7-.3 2-5.1 2.6-6.8-1.6-16-3.8-5.4-1.3h-.8v.4l4.6 4.5 8.3 7.5L89 80.1l.5 2.4-1.3 2-1.4-.2-9.2-7-3.6-3-8-6.8h-.5v.7l1.8 2.7 9.8 14.7.5 4.5-.7 1.4-2.6 1-2.7-.6-5.8-8-6-9-4.7-8.2-.5.4-2.9 30.2-1.3 1.5-3 1.2-2.5-2-1.4-3 1.4-6.2 1.6-8 1.3-6.4 1.2-7.9.7-2.6v-.2H49L43 72l-9 12.3-7.2 7.6-1.7.7-3-1.5.3-2.8L24 86l10-12.8 6-7.9 4-4.6-.1-.5h-.3L17.2 77.4l-4.7.6-2-2 .2-3 1-1 8-5.5Z" />
      </svg>
    );
  }
  if (k === 'mistral') {
    return <MistralLogo size={size} />;
  }
  if (k === 'ai_overview' || k === 'google_ai_mode') {
    return <AiOverviewLogo size={size} />;
  }
  return (
    <Box
      sx={{
        width: size,
        height: size,
        borderRadius: '50%',
        bgcolor: meta.bg,
        color: meta.color,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: size * 0.55,
        fontWeight: 700,
      }}
    >
      ?
    </Box>
  );
}

export default function BrandAxisPage({ axis }: { axis: BrandQueryAxis }) {
  const meta = AXIS_META[axis];
  const { t } = useTranslation();
  const metaTitle = t(meta.titleKey);
  const { activeBrand } = useBrand();
  const { tenant } = useAuth();
  // Manual run-now is agency-only (others cadence-driven; API enforces, allowing
  // only the onboarding first-run).
  const isAgency = tenant?.tier === 'agency';
  const [queries, setQueries] = useState<BrandQuery[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  // The agency « Nouvel audit » opens the brand-wide LaunchDialog (00b DQ13); since the ship-4
  // flip a tenant still on 'legacy' reads the dialog's own `.launch.legacy` line (no per-axis dialog).
  const [launchOpen, setLaunchOpen] = useState(false);
  const [configOpen, setConfigOpen] = useState(false);
  const [scoreInfoAnchor, setScoreInfoAnchor] = useState<HTMLElement | null>(null);
  const [latestAudit, setLatestAudit] = useState<Audit | null>(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const [footerAutoOpen, setFooterAutoOpen] = useState(() => searchParams.get('studio') === 'footer');
  // Back from Google (`?gsc=<result>`): the popup reopens on the composer's Search Console source.
  const [gscReturnOpen, setGscReturnOpen] = useState(false);

  /**
   * `Configurer │ Tableau de bord`, same contract as Conversations: the URL wins so a
   * shared link lands where it says, localStorage remembers the last choice, and a
   * bare `/brand/<axis>` from the nav lands on Configurer.
   */
  const storedView = localStorage.getItem('traaker.brandAxis.view');
  const view: 'dashboard' | 'setup' =
    searchParams.get('view') === 'dashboard'
      ? 'dashboard'
      : storedView === 'dashboard'
        ? 'dashboard'
        : 'setup';
  const setView = useCallback(
    (v: 'dashboard' | 'setup') => {
      localStorage.setItem('traaker.brandAxis.view', v);
      setSearchParams(
        (prev) => {
          const p = new URLSearchParams(prev);
          if (v === 'dashboard') p.set('view', v);
          else p.delete('view');
          return p;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );

  // The real enforced cap is the tenant-wide `prompts` gauge (all brands +
  // axes), surfaced in the popup. The button just shows this axis's active
  // count for quick context — mixing a per-axis count with a tenant-wide cap
  // would be misleading.

  // `queries` holds active prompts only (drives KPI counts + create gating);
  // `archivedQueries` holds archived rows so the matrix can offer re-activate.
  const [archivedQueries, setArchivedQueries] = useState<BrandQuery[]>([]);
  // The most recent audit of any status (running/pending/completed/failed) —
  // separate from `latestAudit` (the latest *completed* audit, which carries the
  // axis summary). Drives the "audit en cours" banner.
  const [inFlightAudit, setInFlightAudit] = useState<Audit | null>(null);
  // Live fan-out completion progress for the in-flight audit (3 split axes only).
  const [progress, setProgress] = useState<AuditProgress | null>(null);

  // `background: true` skips the full-page loading spinner — used by the in-flight
  // progress poll so a mid-audit refresh never blanks the page to a spinner. Only
  // the initial mount (and brand switch) shows the full-page CircularProgress.
  const reload = useCallback(async (opts?: { background?: boolean }) => {
    const background = opts?.background === true;
    if (!activeBrand?.brandId) {
      setQueries([]);
      setArchivedQueries([]);
      setLatestAudit(null);
      setInFlightAudit(null);
      setLoading(false);
      return;
    }
    if (!background) setLoading(true);
    try {
      const [allQueries, allAudits] = await Promise.all([
        brandQueriesApi.list(activeBrand.brandId, axis),
        // Newest-first via the type-date GSI, every status. Feeds both the
        // "audit en cours" banner and the latest-completed hydration below; the
        // prompt matrix reads `BPPROMPT` directly and needs neither (lot 8).
        auditsApi.list(meta.auditType, activeBrand.brandId).catch(() => [] as Audit[]),
      ]);
      setQueries(allQueries.filter((q) => q.status === 'active'));
      setArchivedQueries(allQueries.filter((q) => q.status === 'archived'));

      // Most recent audit by createdAt regardless of status — the API list
      // returns newest-first via the type-date GSI, so head of array wins.
      const mostRecent = allAudits[0] || null;
      setInFlightAudit(
        mostRecent && (mostRecent.status === 'pending' || mostRecent.status === 'running')
          ? mostRecent
          : null,
      );

      // Hydrate the latest *completed* Audit row — it carries the axis summary
      // (recommendations, referral zone breakdown). The list projection may omit
      // the summaries, so keep the thin row on a failed GetItem rather than
      // dropping the whole block: we know the audit exists.
      const lastCompleted = allAudits.find((a) => a.status === 'completed') || null;
      if (lastCompleted) {
        try {
          const a = await auditsApi.getById(lastCompleted.auditId);
          setLatestAudit(a || lastCompleted);
        } catch {
          setLatestAudit(lastCompleted);
        }
      } else {
        setLatestAudit(null);
      }
      setError('');
    } catch (err: any) {
      setError(err?.message || t('axisPage.error.load'));
    } finally {
      setLoading(false);
    }
  }, [activeBrand?.brandId, axis, meta.auditType, t]);

  // Single in-flight loop: poll /progress every 4s for the live percentage AND
  // trigger ONE matrix reload the moment progress reports `done` — so the
  // results refresh + banner clears within ~4s of completion (no separate blind
  // 20s timer needed). Visibility-aware. Best-effort: a failed poll keeps the
  // last bar value.
  useEffect(() => {
    if (!inFlightAudit) {
      setProgress(null);
      return;
    }
    let cancelled = false;
    const poll = async () => {
      if (document.visibilityState !== 'visible') return;
      try {
        const p = await auditsApi.getProgress(inFlightAudit.auditId, meta.auditType as BrandAxisAuditType);
        if (cancelled) return;
        setProgress(p);
        // On `done`, reload to refresh matrix/summary + re-derive inFlightAudit.
        // We DON'T one-shot-guard this: AGENT_DONE (→ progress=done) lands a beat
        // before the phase-checker flips the audit row to `completed`, so the
        // first reload may still see `running`. Reloading each `done` poll
        // self-terminates once the row flips → inFlightAudit clears → this effect
        // tears down and polling stops (typically 1 extra reload, ~4s).
        if (p.state === 'done') {
          reload({ background: true }); // refresh results without blanking the page
        }
      } catch {
        /* leave the last value */
      }
    };
    poll();
    const id = window.setInterval(poll, 4_000);
    return () => {
      cancelled = true;
      window.clearInterval(id);
    };
  }, [inFlightAudit, meta.auditType, reload]);

  useEffect(() => {
    reload();
  }, [reload]);

  // ?studio=footer: open the Prompt Studio with the footer popover auto-open
  useEffect(() => {
    if (searchParams.get('studio') !== 'footer') return;
    setConfigOpen(true);
    setSearchParams(
      (p) => {
        const n = new URLSearchParams(p);
        n.delete('studio');
        return n;
      },
      { replace: true },
    );
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ?gsc=<result>: the Search Console OAuth return (lot H step 13). Strip it, hand the
  // result to the band once (it shows its line and reloads the status), and reopen the
  // popup on the composer with the Search Console source.
  useEffect(() => {
    const result = searchParams.get('gsc');
    if (result === null) return;
    setSearchParams(
      (p) => {
        const n = new URLSearchParams(p);
        n.delete('gsc');
        return n;
      },
      { replace: true },
    );
    if (!isGscCallbackResult(result)) return;
    setPendingGscReturn(result);
    setGscReturnOpen(true);
    setConfigOpen(true);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  const summary: BrandNarrativeSummary | BrandAuthoritySummary | BrandReferralSummary | undefined =
    axis === 'narrative'
      ? latestAudit?.brandNarrativeSummary
      : axis === 'authority'
        ? latestAudit?.brandAuthoritySummary
        : latestAudit?.brandReferralSummary;
  const recosV2: BrandAxisRecommendation[] = (summary as any)?.recommendations_v2 || [];
  const recosLegacy: string[] = summary?.recommendations || [];
  const auditTypeForAxis: AuditType =
    axis === 'narrative' ? 'brand_narrative' : axis === 'authority' ? 'brand_authority' : 'brand_referral';
  const unifiedRecos: UnifiedRecommendation[] = recosV2.length
    ? fromBrandAxis(recosV2, { auditType: auditTypeForAxis, originLabel: metaTitle })
    : fromStringList(recosLegacy, { auditType: auditTypeForAxis, originLabel: metaTitle });

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Box sx={{ width: 12, height: 12, borderRadius: 0.6, bgcolor: meta.color }} />
          <Typography variant="h4" fontWeight={700} data-tour="page-intro">
            {metaTitle}
          </Typography>
          <IconButton
            size="small"
            aria-label={t('axisPage.scoreInfo.aria')}
            onClick={(e) => setScoreInfoAnchor(e.currentTarget)}
            sx={{ color: 'text.secondary' }}
          >
            <InfoOutlinedIcon fontSize="small" />
          </IconButton>
          <ScoreInfoPopover
            axis={axis}
            color={meta.color}
            anchorEl={scoreInfoAnchor}
            onClose={() => setScoreInfoAnchor(null)}
          />
        </Box>
        {/* ONE period control for this page, and it lives inside the dashboard. The view
            switch is NOT here either — it sits on its own row under the subtitle, where
            it reads as the lens over the page rather than as a third header action. */}
        <Stack direction="row" spacing={1} alignItems="center">
          {/* The popup opened on its footer Prompts popover (cadence + engines), as `?studio=footer`. */}
          <Button
            data-tour="cadence-models"
            variant="outlined"
            startIcon={<ScheduleIcon />}
            onClick={() => { setFooterAutoOpen(true); setConfigOpen(true); }}
            disabled={!activeBrand?.brandId}
          >
            {t('axisPage.cadenceModels')}
          </Button>
          <Button
            data-tour="prompt-config"
            variant="outlined"
            startIcon={<SettingsIcon />}
            onClick={() => setConfigOpen(true)}
            disabled={!activeBrand?.brandId}
          >
            {t('axisPage.configurePrompts', { count: queries.length })}
          </Button>
          {isAgency && (
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => setLaunchOpen(true)}
              disabled={!activeBrand?.brandId || queries.length === 0}
            >
              {t('axisPage.newAudit')}
            </Button>
          )}
        </Stack>
      </Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        {t(meta.subtitleKey)}
      </Typography>

      <SegmentedControl
        value={view}
        onChange={setView}
        ariaLabel={t('axisPage.viewSwitchAria')}
        options={[
          { value: 'setup', label: t('common.viewSetup'), icon: <TuneIcon /> },
          { value: 'dashboard', label: t('common.viewDashboard'), icon: <BarChartIcon /> },
        ]}
        sx={{ mb: 2.5 }}
      />

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {/* Empty-state gate: must configure at least one prompt before audits are useful. */}
      {queries.length === 0 ? (
        <Card>
          <CardContent sx={{ p: 5, textAlign: 'center' }}>
            <Typography variant="h6" sx={{ mb: 1 }}>
              {t('axisPage.empty.title')}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3, maxWidth: 520, mx: 'auto' }}>
              {t('axisPage.empty.description')}
            </Typography>
            <Button
              variant="contained"
              startIcon={<SettingsIcon />}
              onClick={() => setConfigOpen(true)}
              disabled={!activeBrand?.brandId}
            >
              {t('axisPage.empty.cta')}
            </Button>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 2 }}>
              {t('axisPage.empty.hint')}
            </Typography>
          </CardContent>
        </Card>
      ) : view === 'dashboard' ? (
        activeBrand?.brandId ? <BrandAxisDashboard brandId={activeBrand.brandId} axis={axis} /> : null
      ) : (
        <>
          {inFlightAudit && (
            <Card sx={{ mb: 3, borderLeft: `4px solid ${meta.color}` }}>
              <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2, py: '14px !important' }}>
                <CircularProgress size={22} sx={{ color: meta.color }} />
                <Box sx={{ flex: 1 }}>
                  <Typography variant="subtitle2" fontWeight={700} sx={{ color: meta.color }}>
                    {t('axisPage.inProgress.title', { axis: metaTitle.toLowerCase() })}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {progress && progress.state === 'running' && progress.expected > 0
                      ? t('axisPage.inProgress.running', {
                          completed: progress.completed,
                          expected: progress.expected,
                        })
                      : t('axisPage.inProgress.started', {
                          date: formatDateTime(inFlightAudit.createdAt, {
                            day: '2-digit',
                            month: 'short',
                            hour: '2-digit',
                            minute: '2-digit',
                          }),
                        })}
                  </Typography>
                </Box>
                <Chip
                  label={
                    progress && progress.state === 'running' && progress.expected > 0
                      ? `${progress.pct}%`
                      : inFlightAudit.status === 'pending'
                        ? t('axisPage.status.pending')
                        : t('axisPage.status.running')
                  }
                  size="small"
                  sx={{
                    bgcolor: `${meta.color}1A`,
                    color: meta.color,
                    fontWeight: 700,
                  }}
                />
              </CardContent>
              <LinearProgress
                {...(progress && progress.state === 'running' && progress.expected > 0
                  ? { variant: 'determinate' as const, value: progress.pct }
                  : {})}
                sx={{
                  height: 3,
                  bgcolor: `${meta.color}22`,
                  '& .MuiLinearProgress-bar': { bgcolor: meta.color },
                }}
              />
            </Card>
          )}

          {/* Narrative « Perception IA » is back HERE (2026-10-08), off the last
              audit's own summary. In the dashboard it sat under a window heading while
              being prose about a single run — and once the window could reach two
              years, one audit's sentences under « 2 dernières années » was simply
              false. Authority « Autorité thématique » stays in the dashboard: it is a
              topic COUNT, which does aggregate over a period. The referral zone
              breakdown has no dashboard equivalent and stays here too. */}
          {axis === 'narrative' && summary && (
            <AxisPerceptionCard summary={summary as BrandNarrativeSummary} color={meta.color} />
          )}
          {axis === 'referral' && summary && (
            <AxisZoneSummary summary={summary as BrandReferralSummary} color={meta.color} />
          )}

          {latestAudit ? (
            <AxisMatrixTable
              axis={axis}
              queries={queries}
              archivedQueries={archivedQueries}
              latestAudit={latestAudit}
              latestAuditMeta={meta}
              brand={activeBrand}
              onReactivate={async (queryId) => {
                if (!activeBrand?.brandId) return;
                try {
                  await brandQueriesApi.update(activeBrand.brandId, queryId, {
                    axis,
                    status: 'active',
                  });
                  await reload();
                } catch (err: any) {
                  setError(err?.message || t('axisPage.error.reactivate'));
                }
              }}
              onArchive={async (queryId) => {
                if (!activeBrand?.brandId) return;
                try {
                  await brandQueriesApi.archive(activeBrand.brandId, axis, queryId);
                  await reload();
                } catch (err: any) {
                  setError(err?.message || t('axisPage.error.archive'));
                }
              }}
            />
          ) : (
            <Card>
              <CardContent sx={{ p: 4, textAlign: 'center' }}>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  {isAgency
                    ? t('axisPage.noAudit.agency')
                    : t('axisPage.noAudit.selfServe')}
                </Typography>
                {isAgency && (
                  <Button
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={() => setLaunchOpen(true)}
                    disabled={!activeBrand?.brandId}
                  >
                    {t('axisPage.noAudit.cta')}
                  </Button>
                )}
              </CardContent>
            </Card>
          )}

          {unifiedRecos.length > 0 && (
            <Box sx={{ mt: 3 }}>
              <RecommendationList
                recommendations={unifiedRecos}
                auditId={latestAudit?.auditId || ''}
                brandId={activeBrand?.brandId}
                subtitle={t('axisPage.recos.subtitle')}
              />
            </Box>
          )}
        </>
      )}

      {/* Prompt Studio dialog (lot B step 4) — replaces QueryManager */}
      {activeBrand && (
        <PromptStudioDialog
          open={configOpen}
          onClose={() => { setConfigOpen(false); setFooterAutoOpen(false); setGscReturnOpen(false); }}
          tenant={tenant}
          brand={activeBrand}
          initialTab="prompts"
          // Once, on close; background so the page behind never swaps to its spinner.
          onChanged={() => reload({ background: true })}
          initialFooterOpen={footerAutoOpen}
          initialSource={gscReturnOpen ? 'gsc' : undefined}
          searchConsole={{ isAgency }}
        />
      )}

      {/* Mounted per open, so each open starts from fresh previews. */}
      {launchOpen && activeBrand?.brandId && (
        <LaunchDialog
          open
          onClose={() => {
            setLaunchOpen(false);
            reload({ background: true });
          }}
          brandId={activeBrand.brandId}
          brandName={activeBrand.name}
          onLaunched={() => reload({ background: true })}
        />
      )}

    </Box>
  );
}

/**
 * Customer-facing "how is this score computed?" popover, anchored to the small
 * ⓘ next to the axis title. Plain-language mirror of the deterministic per-axis
 * formula in the agent's scorer — no engine names, no internal weighting jargon.
 */
function ScoreInfoPopover({
  axis,
  color,
  anchorEl,
  onClose,
}: {
  axis: BrandQueryAxis;
  color: string;
  anchorEl: HTMLElement | null;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const rows = t(`axisPage.scoreInfo.${axis}.rows`, { returnObjects: true }) as string[];
  return (
    <Popover
      open={Boolean(anchorEl)}
      anchorEl={anchorEl}
      onClose={onClose}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      slotProps={{ paper: { sx: { maxWidth: 380, p: 2.5, borderRadius: 2 } } }}
    >
      <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 0.5, color }}>
        {t('axisPage.scoreInfo.title')}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
        {t(`axisPage.scoreInfo.${axis}.intro`)}
      </Typography>
      <Stack spacing={0.75}>
        {Array.isArray(rows) &&
          rows.map((line, i) => (
            <Box key={i} sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
              <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: color, mt: '6px', flexShrink: 0 }} />
              <Typography variant="body2">{line}</Typography>
            </Box>
          ))}
      </Stack>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1.5 }}>
        {t('axisPage.scoreInfo.footer')}
      </Typography>
    </Popover>
  );
}

// ── Referral zone breakdown (sits between the KPIs and the matrix) ──

/**
 * The one per-audit summary block that stayed on this page: the referral zone scores
 * have no windowed equivalent in the dashboard. Narrative « Perception IA » and
 * authority « Autorité thématique » moved there (lot 4) and are re-presented over the
 * whole period rather than as chip clouds off the last audit.
 */
/**
 * « Perception IA » — the narrative axis's prose lede: one key sentence plus the pros
 * and cons the judge extracted.
 *
 * Scoped to the LAST COMPLETED AUDIT, deliberately, and that is the whole reason it
 * lives here rather than in the dashboard: it is not a measurement that folds over a
 * period. The dashboard's version read « la perception » over the selected window
 * while actually showing the newest audit's sentences inside it.
 *
 * Absent fields render NOTHING rather than an empty card: a run whose judge produced
 * no summary is not a brand with no perception.
 */
function AxisPerceptionCard({
  summary,
  color,
}: {
  summary: BrandNarrativeSummary;
  color: string;
}) {
  const { t } = useTranslation();
  const perception = (summary.key_perception || '').trim();
  const pros = (summary.pros ?? []).filter((x) => String(x).trim());
  const cons = (summary.cons ?? []).filter((x) => String(x).trim());
  if (!perception && !pros.length && !cons.length) return null;

  return (
    <Card sx={{ mb: 3 }}>
      <CardContent>
        <Typography
          component="h3"
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            m: 0,
            mb: 1.5,
            fontSize: 13,
            fontWeight: 600,
            letterSpacing: '0.09em',
            textTransform: 'uppercase',
            color: 'text.secondary',
          }}
        >
          <Box component="span" sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: color }} />
          {t('bpDash.perceptionTitle')}
        </Typography>
        {perception && (
          <Typography variant="body1" sx={{ mb: pros.length || cons.length ? 2.5 : 0 }}>
            {perception}
          </Typography>
        )}
        {(pros.length > 0 || cons.length > 0) && (
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 3 }}>
            {[
              { items: pros, label: t('bpDash.perceptionStrengths'), tone: '#3F9A6B', Icon: CheckCircleOutlineIcon },
              { items: cons, label: t('bpDash.perceptionWeaknesses'), tone: '#D9483B', Icon: ErrorOutlineIcon },
            ]
              .filter((col) => col.items.length > 0)
              .map((col) => (
                <Box key={col.label}>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ display: 'block', mb: 0.75, fontWeight: 600 }}
                  >
                    {col.label}
                  </Typography>
                  {col.items.map((it, i) => (
                    <Box key={i} sx={{ display: 'flex', gap: 1, alignItems: 'flex-start', mb: 0.75 }}>
                      <col.Icon sx={{ fontSize: 17, color: col.tone, mt: '2px', flexShrink: 0 }} />
                      <Typography variant="body2">{it}</Typography>
                    </Box>
                  ))}
                </Box>
              ))}
          </Box>
        )}
      </CardContent>
    </Card>
  );
}

function AxisZoneSummary({
  summary,
  color,
}: {
  summary: BrandReferralSummary;
  color: string;
}) {
  const { t } = useTranslation();
  const zones = summary.zones ? Object.entries(summary.zones) : [];
  if (zones.length === 0) return null;
  return (
    <Card sx={{ mb: 3 }}>
      <CardContent>
        <Typography variant="overline" color="text.secondary" fontWeight={700} sx={{ letterSpacing: 0.5 }}>
          {t('axisPage.summary.visibilityByZone')}
        </Typography>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1 }}>
          {zones.map(([zone, data]) => (
            <Chip
              key={zone}
              label={`${zone} · ${data.score}/100`}
              sx={{
                bgcolor: `${color}1A`,
                color,
                fontWeight: 700,
                fontSize: '0.78rem',
              }}
            />
          ))}
        </Box>
      </CardContent>
    </Card>
  );
}

// ── Matrix table ──

function scoreCellColor(score: number | undefined | null): { bg: string; fg: string } {
  if (score == null) return { bg: 'transparent', fg: '#D1D5DB' };
  if (score >= 70) return { bg: '#DCFCE7', fg: '#15803D' };
  if (score >= 40) return { bg: '#FEF3C7', fg: '#92400E' };
  return { bg: '#FEE2E2', fg: '#991B1B' };
}

/** Stable dismissal id for a "Créer du contenu" action on an authority prompt.
 *  Reuses the per-brand `dismissed-recommendations` store as per-prompt metadata,
 *  so once the topic is created the button never reappears (even after refresh). */
function contentActionId(queryTextHash: string) {
  return `authority-content:${queryTextHash}`;
}

/**
 * Two low-friction actions surfaced on an under-performing authority prompt,
 * shown inside the expanded panel (keeps the collapsed row compact):
 *  - "Créer du contenu" saves the prompt as a reusable Studio content topic
 *    (same content-target path as RecommendationCard — saves, does NOT generate).
 *    Persisted per prompt: once created it stays hidden across refreshes.
 *  - "Ajuster la stratégie" opens the shared two-layer Brand Knowledge + GEO
 *    diff dialog seeded with the prompt (POST /brands/:id/strategy/suggest).
 *    Repeatable, so it's always available.
 */
function AuthorityPromptActions({
  brandId,
  auditId,
  promptText,
  contentCreated,
  onContentCreated,
}: {
  brandId?: string;
  auditId?: string;
  promptText: string;
  /** Whether a content topic was already created for this prompt (persisted). */
  contentCreated: boolean;
  onContentCreated: () => void;
}) {
  const { tenant } = useAuth();
  const toast = useToast();
  const navigate = useNavigate();
  const [busy, setBusy] = useState(false);
  const [geoOpen, setGeoOpen] = useState(false);

  const newsWatchEnabled = !!tenant?.settings?.featureFlags?.newsWatchEnabled;

  const addContentTopic = async () => {
    if (busy || contentCreated || !brandId) return;
    setBusy(true);
    try {
      await proposedArticlesApi.createContentTopic({
        brandId,
        topic: promptText,
        originLabel: `Autorité · requête à faible score`,
        sourceAuditId: auditId,
      });
      onContentCreated();
      toast.show('Ajouté au studio de création', {
        action: { label: 'Voir', onClick: () => navigate('/news-watch', { state: { focusReco: true } }) },
      });
    } catch {
      toast.show("L'action n'a pas pu être enregistrée.", { severity: 'error' });
    } finally {
      setBusy(false);
    }
  };

  return (
    <Box sx={{ display: 'flex', gap: 1, mb: 1.5, flexWrap: 'wrap' }}>
      {newsWatchEnabled && brandId && !contentCreated && (
        <Button
          size="small"
          variant="outlined"
          disabled={busy}
          startIcon={busy ? <CircularProgress size={14} color="inherit" /> : <EditNoteIcon sx={{ fontSize: 16 }} />}
          onClick={addContentTopic}
          sx={{
            textTransform: 'none',
            fontWeight: 600,
            color: '#0284C7',
            borderColor: 'rgba(2,132,199,0.4)',
            '&:hover': { borderColor: '#0284C7', bgcolor: 'rgba(2,132,199,0.06)' },
          }}
        >
          Créer du contenu
        </Button>
      )}
      {brandId && (
        <Button
          size="small"
          variant="outlined"
          startIcon={<TuneIcon sx={{ fontSize: 16 }} />}
          onClick={() => setGeoOpen(true)}
          sx={{
            textTransform: 'none',
            fontWeight: 600,
            color: '#B45309',
            borderColor: 'rgba(180,83,9,0.4)',
            '&:hover': { borderColor: '#B45309', bgcolor: 'rgba(180,83,9,0.06)' },
          }}
        >
          Ajuster la stratégie
        </Button>
      )}
      {geoOpen && brandId && (
        <StrategySuggestDialog
          open={geoOpen}
          onClose={() => setGeoOpen(false)}
          brandId={brandId}
          auditId={auditId}
          seedQuery={promptText}
          seedResponse={promptText}
          axis="authority"
          onApplied={() => toast.show('Stratégie mise à jour')}
        />
      )}
    </Box>
  );
}

/** Prompt scores at/above this are "good" (green band); below is low. */
const LOW_SCORE_THRESHOLD = 70;

/** Row grouping for the grid. Segmenting re-orders and labels; it narrows nothing
 *  (the prompt-tag filter is the control that narrows). */
type MatrixSegmentBy = 'none' | 'tag' | 'zone' | 'language' | 'level' | 'topic';

interface MatrixRow {
  /** E13: the row identity — `promptIdentityKey(textHash, place, language)` from
   *  the API (`row.key`, E12) for API rows, or computed locally for local rows.
   *  Used as the state map key for `expanded`, `selectedCols`, `reactivating`,
   *  `selectedEngineByQuery`. `queryTextHash` is kept for `contentActionId`. */
  key: string;
  /** All queryIds folded into this row (from the API `row.qids`, E12). */
  qids: string[];
  queryTextHash: string;
  text: string;
  queryId?: string;
  archived: boolean;
  canReactivate: boolean;
  /** The prompt row is gone from DynamoDB: no text, no tags, nothing to re-post. */
  unknownPrompt: boolean;
  /** Measured in the window but no longer a configured prompt of this axis. */
  notConfigured: boolean;
  level?: string;
  zone?: string;
  language?: string;
  topic?: string;
  tags?: string[];
  /** Index-aligned to `columns` — `null` = that bucket did not measure this prompt. */
  cells: (BPPromptFact | null)[];
}

/**
 * The prompt × period grid, driven by `GET …/brand-perception/dashboard/prompts`
 * (plan §5.3).
 *
 * It used to read `/audits/:brandId/matrix/:type`, which spends ONE `GetItem` on the
 * fat `AGENT_DONE#brand_<axis>` summary PER audit (the page asked for 12) and can
 * never reach past the audit list's own window. The dashboard endpoint answers the
 * whole window with ONE `SK BETWEEN` range query over `BPPROMPT#<axis>#<day>`
 * (`BPPROMPTM#` above 90 days) plus one prompt-table query for the text/tag join, so
 * the grid now follows the same timeframe selector as the rest of the dashboard and
 * costs less over 90 days than it used to over 12 audits.
 *
 * What the endpoint imposes on this UI:
 *  - Rows join across columns on `queryTextHash` (`fact.h`), NOT on `queryId`: a
 *    prompt archived and recreated with the same text keeps ONE history row.
 *    `queryId` only drives the archive / reactivate buttons.
 *  - A bucket with no audit has no row, so it has no column. Never synthesise an
 *    empty column at 0 — the grid is the list of buckets that were measured.
 *  - Above 90 days the service answers from the MONTHLY family, where one column
 *    aggregates several audits. The per-engine drill-down can only show one of them
 *    (`column.auditId` = the last audit of that month), so the panel names it. No
 *    caller can currently request such a window (`maxDays={BP_MAX_SPAN_DAYS}`), but
 *    the behaviour is implemented so lot 9 need not come back here.
 *  - Absent is absent: a missing cell or rate renders as « — », never as 0.
 */
function AxisMatrixTable({
  axis,
  queries,
  archivedQueries,
  latestAudit,
  latestAuditMeta,
  brand,
  onReactivate,
  onArchive,
}: {
  axis: BrandQueryAxis;
  queries: BrandQuery[];
  archivedQueries: BrandQuery[];
  latestAudit: Audit;
  latestAuditMeta: typeof AXIS_META[BrandQueryAxis];
  brand: any;
  onReactivate: (queryId: string) => Promise<void>;
  onArchive: (queryId: string) => Promise<void>;
}) {
  const { t, i18n } = useTranslation();
  const brandId: string | undefined = brand?.brandId;

  // ── Period + prompt-tag controls, driven like the dashboard's ──
  // Literally the same selection as the dashboard's, not merely the same shape: one
  // `useBpTimeframe` behind both, so switching view or axis keeps the window.
  const [timeframe, setTimeframe] = useBpTimeframe();
  const range = useMemo(() => resolveBpRange(timeframe), [timeframe]);
  const [tagFilter, setTagFilter] = useState<string[]>([]);
  const [tagOptions, setTagOptions] = useState<string[]>([]);
  const [segmentBy, setSegmentBy] = useState<MatrixSegmentBy>('none');

  const [data, setData] = useState<BPPromptMatrixResponse | null>(null);
  const [loadError, setLoadError] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!brandId) return;
    let cancelled = false;
    brandQueriesApi
      .listTags(brandId)
      .then((r) => { if (!cancelled) setTagOptions((r?.tags ?? []).map((x) => x.name)); })
      .catch(() => { if (!cancelled) setTagOptions([]); });
    return () => { cancelled = true; };
  }, [brandId]);

  // The one range query. Debounced like the dashboard so dragging the date pickers
  // does not fan out a request per keystroke.
  const tagKey = tagFilter.join(',');
  useEffect(() => {
    if (!brandId) return;
    let cancelled = false;
    const params = new URLSearchParams({ axis, from: range.from, to: range.to });
    if (tagKey) params.set('tags', tagKey);
    const timer = window.setTimeout(() => {
      setLoading(true);
      api
        .get<BPPromptMatrixResponse>(
          `/brands/${brandId}/brand-perception/dashboard/prompts?${params.toString()}`,
        )
        .then((r) => {
          if (cancelled) return;
          setData(r ?? null);
          setLoadError('');
        })
        .catch((err: any) => {
          // No in-band fallback: an empty grid would read as « rien n'a été mesuré ».
          if (cancelled) return;
          setData(null);
          setLoadError(err?.message || t('bpMatrix.loadError'));
        })
        .finally(() => { if (!cancelled) setLoading(false); });
    }, 300);
    return () => { cancelled = true; window.clearTimeout(timer); };
  }, [brandId, axis, range.from, range.to, tagKey, t]);

  const columns = useMemo(() => data?.columns ?? [], [data]);
  const grain = data?.grain ?? 'day';
  /** The service's own signal that the window outran the daily tier's retention. */
  const detailExpired = (data?.caveats ?? []).includes('monthly_tier_empty');
  const lastColumnDay = columns.length ? columns[columns.length - 1].day : undefined;

  /** A bucket label: `YYYY-MM-DD` for the daily tier, `YYYY-MM` for the monthly one. */
  const bucketLabel = useCallback(
    (day: string) =>
      day.length === 7
        ? new Date(`${day}-01T00:00:00.000Z`).toLocaleDateString(i18n.language, {
            month: 'short',
            year: 'numeric', // « nov. 2026 », never « nov. 26 » (read as a day)
            timeZone: 'UTC',
          })
        : new Date(`${day}T00:00:00.000Z`).toLocaleDateString(i18n.language, {
            day: '2-digit',
            month: 'short',
            timeZone: 'UTC',
          }),
    [i18n.language],
  );

  // Rows: the API's per-key cells (E13: keyed on `row.key`, not `row.h`), joined
  // with the live `queries` / `archivedQueries` for chips and archive button.
  const rows = useMemo<MatrixRow[]>(() => {
    const apiRows = data?.rows ?? [];
    const nCols = columns.length;
    // E13: key on r.key (promptIdentityKey from the API).
    const byApiKey = new Map(apiRows.map((r) => [r.key, r]));
    const emptyCells = () => new Array<BPPromptFact | null>(nCols).fill(null);
    // The tag filter is applied server-side on the API's own view of a prompt's tags;
    // mirror it on the rows we add locally so the two halves of the grid agree.
    const tagsMatch = (tags?: string[]) =>
      tagFilter.length === 0 ||
      (tags ?? []).some((x) => tagFilter.some((f) => f.toLowerCase() === x.toLowerCase()));

    const byKey = new Map<string, MatrixRow>();
    const fromQuery = (q: BrandQuery, archived: boolean): MatrixRow => {
      // E13: local key uses promptIdentityKey so it matches the API's key.
      const localKey = promptIdentityKey(q.textHash!, storedPlaceKey(q), languageKeyOf(q, brand));
      const apiRow = byApiKey.get(localKey);
      return {
        key: localKey,
        qids: apiRow?.qids ?? (q.queryId ? [q.queryId] : []),
        queryTextHash: q.textHash!,
        text: q.text,
        queryId: q.queryId,
        archived,
        canReactivate: archived,
        unknownPrompt: false,
        notConfigured: false,
        level: q.level && q.level !== 'pending' ? q.level : undefined,
        zone: q.zone,
        language: q.language,
        topic: q.topic,
        tags: q.tags,
        cells: apiRow?.cells ?? emptyCells(),
      };
    };

    for (const q of queries) {
      if (!q.textHash || !tagsMatch(q.tags)) continue;
      const localKey = promptIdentityKey(q.textHash!, storedPlaceKey(q), languageKeyOf(q, brand));
      byKey.set(localKey, fromQuery(q, false));
    }
    // An archived prompt shows up only when the window actually measured it —
    // otherwise every column is empty and the row is pure noise. The full archived
    // list stays reachable from the Configure modal.
    for (const q of archivedQueries) {
      if (!q.textHash) continue;
      const localKey = promptIdentityKey(q.textHash!, storedPlaceKey(q), languageKeyOf(q, brand));
      if (byKey.has(localKey)) continue;
      if (!byApiKey.has(localKey) || !tagsMatch(q.tags)) continue;
      byKey.set(localKey, fromQuery(q, true));
    }
    // Keys the window measured that no current prompt carries: either the prompt
    // was reworded (its `queryId` still resolves, so the API hands us its CURRENT
    // text) or it was hard-deleted (`unknownPrompt` — no text, no tags at all).
    // Both stay as rows because they are in the totals, labelled as what they are
    // and never folded into a « sans tag » bucket. Neither can be re-posted:
    // BPPROMPT stores no prompt TEXT, so the text we hold is not the measured one.
    for (const r of apiRows) {
      if (byKey.has(r.key)) continue;
      byKey.set(r.key, {
        key: r.key,
        qids: r.qids ?? [],
        queryTextHash: r.h,
        text: r.text || t('axisPage.matrix.promptNoText'),
        archived: true,
        canReactivate: false,
        unknownPrompt: r.unknownPrompt === true,
        notConfigured: r.unknownPrompt !== true,
        tags: r.tags,
        cells: r.cells,
      });
    }

    // Group by language → topic → zone, then by the most recent measured score desc.
    // Empty facets sort last via the high sentinel; archived rows always sink.
    const facet = (v?: string) => (v && v.trim() ? v.trim().toLowerCase() : '￿');
    // F step 8d: a fact without `s` is skipped — never sorted as a 0.
    const rowScore = (r: MatrixRow) => lastScore(r.cells) ?? -1;
    return Array.from(byKey.values()).sort((a, b) => {
      if (a.archived !== b.archived) return a.archived ? 1 : -1;
      const byLang = facet(a.language).localeCompare(facet(b.language));
      if (byLang !== 0) return byLang;
      const byTopic = facet(a.topic).localeCompare(facet(b.topic));
      if (byTopic !== 0) return byTopic;
      const byZone = facet(a.zone).localeCompare(facet(b.zone));
      if (byZone !== 0) return byZone;
      return rowScore(b) - rowScore(a);
    });
  }, [data, columns, queries, archivedQueries, tagFilter, t]);

  const [expanded, setExpanded] = useState<string | null>(null);
  const [reactivating, setReactivating] = useState<string | null>(null);
  // Drill-down: per-engine result rows keyed by the audit they came from, so two
  // prompts reading the same column only hit `/audits/:id/results` once.
  const [resultsByAudit, setResultsByAudit] = useState<Record<string, any[]>>({});
  const [loadingAudit, setLoadingAudit] = useState<string | null>(null);
  // A monthly column's key is only `YYYY-MM`, so the audit's own date has to be read
  // from the audit row before the panel can name it. Daily columns need no read —
  // the column key IS the day.
  const [auditDates, setAuditDates] = useState<Record<string, string>>({});
  // Columns selected for comparison (max 2), per prompt. Keyed by bucket `day`:
  // stable across refetches, and the unit the user actually clicks.
  const [selectedCols, setSelectedCols] = useState<Map<string, Set<string>>>(new Map());
  // Engine selected per prompt (synchronized across that prompt's panels).
  const [selectedEngineByQuery, setSelectedEngineByQuery] = useState<Map<string, EngineKey | null>>(new Map());

  // A new window brings new buckets, so a day selected under the old one may not
  // exist any more. Clearing beats keeping a selection whose panel would claim
  // « cette colonne ne référence aucun audit » about a column that is simply gone.
  useEffect(() => {
    setSelectedCols(new Map());
    setExpanded(null);
  }, [range.from, range.to]);

  // Authority-only: which prompts already have a Studio content topic created.
  // Reuses the per-brand `dismissed-recommendations` store as per-prompt metadata
  // so "Créer du contenu" stays hidden across refreshes.
  const [contentCreated, setContentCreated] = useState<Set<string>>(new Set());
  useEffect(() => {
    if (axis !== 'authority' || !brandId) return;
    let cancelled = false;
    auditsApi
      .listDismissedRecommendations(brandId)
      .then((ids) => { if (!cancelled) setContentCreated(new Set(ids)); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [axis, brandId]);

  const markContentCreated = useCallback(
    (queryTextHash: string) => {
      const id = contentActionId(queryTextHash);
      setContentCreated((prev) => new Set(prev).add(id));
      if (brandId) auditsApi.dismissRecommendation(brandId, id).catch(() => {});
    },
    [brandId],
  );

  // ── Facet filters (language / topic / zone) over the matrix rows ──
  // '__all__' = no filter on that facet. Options are derived from the rows
  // themselves so an axis with no zones (narrative) just doesn't show that
  // dropdown.
  const [langFilter, setLangFilter] = useState('__all__');
  const [topicFilter, setTopicFilter] = useState('__all__');
  const [zoneFilter, setZoneFilter] = useState('__all__');

  const facetOptions = useMemo(() => {
    const langs = new Set<string>();
    const topics = new Set<string>();
    const zones = new Set<string>();
    for (const r of rows) {
      if (r.language) langs.add(r.language);
      if (r.topic) topics.add(r.topic);
      if (r.zone) zones.add(r.zone);
    }
    return {
      languages: Array.from(langs).sort(),
      topics: Array.from(topics).sort(),
      zones: Array.from(zones).sort(),
    };
  }, [rows]);

  const visibleRows = useMemo(
    () =>
      rows.filter(
        (r) =>
          (langFilter === '__all__' || r.language === langFilter) &&
          (topicFilter === '__all__' || r.topic === topicFilter) &&
          (zoneFilter === '__all__' || r.zone === zoneFilter),
      ),
    [rows, langFilter, topicFilter, zoneFilter],
  );

  /** Segmentation over the visible row set. A prompt carrying several tags appears
   *  under EACH of its tags — the groups do not add up, which the caveat bar says. */
  const groups = useMemo(() => {
    if (segmentBy === 'none') return [{ key: '__all', label: '', rows: visibleRows }];
    const buckets = new Map<string, MatrixRow[]>();
    for (const r of visibleRows) {
      let keys: string[];
      if (segmentBy === 'tag') {
        keys = r.unknownPrompt ? ['￿unknown'] : r.tags?.length ? r.tags : ['￿untagged'];
      } else {
        const v = r[segmentBy];
        keys = [v && v.trim() ? v : '￿unset'];
      }
      for (const k of keys) {
        const bucket = buckets.get(k);
        if (bucket) bucket.push(r);
        else buckets.set(k, [r]);
      }
    }
    const label = (k: string) =>
      k === '￿unknown'
        ? t('bpMatrix.groupUnknownPrompt')
        : k === '￿untagged'
          ? t('bpMatrix.groupUntagged')
          : k === '￿unset'
            ? t('bpMatrix.groupUnset')
            : segmentBy === 'language'
              ? k.toUpperCase()
              : k;
    return Array.from(buckets.entries())
      .sort((a, b) => a[0].localeCompare(b[0]))
      .map(([key, groupRows]) => ({ key, label: label(key), rows: groupRows }));
  }, [segmentBy, visibleRows, t]);

  /**
   * Loads what the drill-down needs for one column's audit. `needDate` is true only
   * on the monthly tier, where the column key cannot name the audit on its own.
   */
  const ensureAudit = useCallback(
    async (auditId: string, needDate: boolean) => {
      if (!resultsByAudit[auditId]) {
        setLoadingAudit(auditId);
        try {
          const fetched = await auditsApi.getResults(auditId, latestAuditMeta.agentType);
          setResultsByAudit((prev) => ({ ...prev, [auditId]: fetched || [] }));
        } catch {
          setResultsByAudit((prev) => ({ ...prev, [auditId]: [] }));
        } finally {
          setLoadingAudit(null);
        }
      }
      if (needDate && !auditDates[auditId]) {
        try {
          const a = await auditsApi.getById(auditId);
          if (a?.completedAt) setAuditDates((prev) => ({ ...prev, [auditId]: a.completedAt! }));
        } catch {
          /* Leave it absent — the panel says « dernier audit du mois » with no date
             rather than guessing one. */
        }
      }
    },
    [resultsByAudit, auditDates, latestAuditMeta.agentType],
  );

  const handleExpand = useCallback(
    (row: MatrixRow) => {
      // E13: use row.key as state map key
      if (expanded === row.key) {
        setExpanded(null);
        return;
      }
      setExpanded(row.key);
      const existing = selectedCols.get(row.key);
      if (existing) {
        for (const day of existing) {
          const idx = columns.findIndex((c) => c.day === day);
          // A month column has no `auditId`: the cell's own `aid` / `ae` is opened (8d).
          for (const src of cellSources(columns[idx], row.cells[idx])) {
            void ensureAudit(src.auditId, grain === 'month' || columns[idx]?.month === true);
          }
        }
        return;
      }
      // Default to the newest column that measured this prompt AND can be opened —
      // a score-only column (detail expired) has nothing to show.
      let idx = -1;
      for (let i = row.cells.length - 1; i >= 0; i -= 1) {
        if (row.cells[i] && !columns[i]?.scoreOnly) { idx = i; break; }
      }
      const col = idx >= 0 ? columns[idx] : undefined;
      if (!col) {
        setExpanded(null);
        return;
      }
      setSelectedCols((prev) => new Map(prev).set(row.key, new Set([col.day])));
      for (const src of cellSources(col, row.cells[idx])) {
        void ensureAudit(src.auditId, grain === 'month' || col.month === true);
      }
    },
    [expanded, selectedCols, columns, grain, ensureAudit],
  );

  /** Select / deselect one column for comparison (max 2, per prompt). */
  const toggleColumn = useCallback(
    async (row: MatrixRow, day: string) => {
      // E13: use row.key as state map key
      const current = selectedCols.get(row.key) || new Set<string>();
      const adding = !current.has(day) && current.size < 2;
      const idx = columns.findIndex((c) => c.day === day);
      const col = columns[idx];
      if (col?.scoreOnly) return;
      // Lazy-load BEFORE the state flips so the panel never renders an empty audit.
      if (adding) {
        await Promise.all(
          cellSources(col, row.cells[idx]).map((src) =>
            ensureAudit(src.auditId, grain === 'month' || col?.month === true),
          ),
        );
      }
      setSelectedCols((prev) => {
        const next = new Map(prev);
        const updated = new Set(next.get(row.key) || new Set<string>());
        if (updated.has(day)) updated.delete(day);
        else if (updated.size < 2) updated.add(day);
        if (updated.size === 0) {
          next.delete(row.key);
          setExpanded(null);
        } else {
          next.set(row.key, updated);
          setExpanded(row.key);
        }
        return next;
      });
    },
    [selectedCols, columns, grain, ensureAudit],
  );

  /** What the drill-down is actually showing, said out loud. On the monthly tier the
   *  column's score aggregates several audits while the prose is one audit's. */
  const sourceLabel = useCallback(
    (day: string, auditId?: string, month?: boolean) => {
      if (!auditId) return t('bpMatrix.noAuditForColumn');
      const monthLabel = month ? columnLabel({ day, month: true }, i18n.language) : bucketLabel(day);
      if (grain !== 'month' && !month) return t('bpMatrix.sourceDaily', { date: bucketLabel(day) });
      const completedAt = auditDates[auditId];
      return completedAt
        ? t('bpMatrix.sourceMonthly', {
            month: monthLabel,
            date: formatDate(completedAt, { day: '2-digit', month: 'short', year: 'numeric' }),
          })
        : t('bpMatrix.sourceMonthlyNoDate', { month: monthLabel });
    },
    [grain, auditDates, bucketLabel, i18n.language, t],
  );

  // Every caveat that affects the grid, surfaced verbatim — including the ones the
  // client produced (a clamped span) and any code this build does not know yet.
  const caveatLines = useMemo(() => {
    const out: string[] = [];
    if (range.spanClamped) out.push(t('bpDash.caveat.spanClamped'));
    if (data?.clamped) out.push(t('bpDash.caveat.clamped'));
    for (const c of data?.caveats ?? []) {
      // Said on the cells themselves (the « i » on a score that cannot be opened), not
      // as a page-level note.
      if (c === 'prompt_detail_score_only') continue;
      const key = `bpDash.caveat.${c}`;
      out.push(i18n.exists(key) ? t(key) : t('bpMatrix.caveatUnknown', { code: c }));
    }
    if (data?.truncated) out.push(t('bpMatrix.truncated'));
    if (data?.unknownPrompts) out.push(t('bpMatrix.unknownPrompts', { count: data.unknownPrompts }));
    const partial = columns.filter((c) => c.partial).map((c) => bucketLabel(c.day));
    if (partial.length) out.push(t('bpMatrix.partialColumns', { days: partial.join(', ') }));
    if (grain === 'month') out.push(t('bpMatrix.monthHint'));
    if (segmentBy === 'tag') out.push(t('bpMatrix.segmentTagOverlap'));
    return out;
  }, [range.spanClamped, data, columns, grain, segmentBy, bucketLabel, i18n, t]);

  const activeFilterCount =
    (langFilter !== '__all__' ? 1 : 0) +
    (topicFilter !== '__all__' ? 1 : 0) +
    (zoneFilter !== '__all__' ? 1 : 0) +
    (tagFilter.length ? 1 : 0);

  return (
    <Card sx={{ mb: 3, overflow: 'hidden' }}>
      <Box
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'center',
          gap: 1.5,
          px: 2,
          py: 1.5,
          borderBottom: '1px solid rgba(11,11,15,0.08)',
          bgcolor: '#FAFBFC',
        }}
      >
        <TimeframeSelector value={timeframe} onChange={setTimeframe} maxDays={BP_MAX_SPAN_DAYS} />
        <Autocomplete<string, true>
          multiple
          size="small"
          options={tagOptions}
          value={tagFilter}
          onChange={(_, v) => setTagFilter(v)}
          renderInput={(params) => (
            <TextField {...params} label={t('bpMatrix.tagFilter')} placeholder={t('bpMatrix.allTags')} />
          )}
          sx={{ minWidth: 200, maxWidth: 320 }}
        />
        <TextField
          select
          size="small"
          label={t('bpMatrix.segmentBy')}
          value={segmentBy}
          onChange={(e) => setSegmentBy(e.target.value as MatrixSegmentBy)}
          sx={{ minWidth: 150 }}
        >
          {(['none', 'tag', 'language', 'zone', 'level', 'topic'] as MatrixSegmentBy[]).map((s) => (
            <MenuItem key={s} value={s}>
              {t(`bpMatrix.segment.${s}`)}
            </MenuItem>
          ))}
        </TextField>
        {facetOptions.languages.length > 1 && (
          <TextField
            select
            size="small"
            label={t('axisPage.filter.language')}
            value={langFilter}
            onChange={(e) => setLangFilter(e.target.value)}
            sx={{ minWidth: 130 }}
          >
            <MenuItem value="__all__">{t('axisPage.filter.allFeminine')}</MenuItem>
            {facetOptions.languages.map((l) => (
              <MenuItem key={l} value={l}>
                {l.toUpperCase()}
              </MenuItem>
            ))}
          </TextField>
        )}
        {facetOptions.topics.length > 0 && (
          <TextField
            select
            size="small"
            label={axis === 'referral' ? t('axisPage.filter.useCase') : t('axisPage.filter.theme')}
            value={topicFilter}
            onChange={(e) => setTopicFilter(e.target.value)}
            sx={{ minWidth: 180 }}
          >
            <MenuItem value="__all__">{t('axisPage.filter.allMasculine')}</MenuItem>
            {facetOptions.topics.map((tp) => (
              <MenuItem key={tp} value={tp}>
                {tp}
              </MenuItem>
            ))}
          </TextField>
        )}
        {facetOptions.zones.length > 1 && (
          <TextField
            select
            size="small"
            label={t('axisPage.filter.zone')}
            value={zoneFilter}
            onChange={(e) => setZoneFilter(e.target.value)}
            sx={{ minWidth: 130 }}
          >
            <MenuItem value="__all__">{t('axisPage.filter.allFeminine')}</MenuItem>
            {facetOptions.zones.map((z) => (
              <MenuItem key={z} value={z}>
                {z}
              </MenuItem>
            ))}
          </TextField>
        )}
        <Box sx={{ flex: 1 }} />
        {loading && <CircularProgress size={16} />}
        <Typography variant="caption" color="text.secondary">
          {t('axisPage.filter.promptCount', { visible: visibleRows.length, total: rows.length })}
        </Typography>
        {activeFilterCount > 0 && (
          <Button
            size="small"
            variant="text"
            onClick={() => {
              setLangFilter('__all__');
              setTopicFilter('__all__');
              setZoneFilter('__all__');
              setTagFilter([]);
            }}
            sx={{ textTransform: 'none' }}
          >
            {t('axisPage.filter.reset')}
          </Button>
        )}
      </Box>

      {loadError && (
        <Alert severity="error" sx={{ borderRadius: 0 }}>
          {loadError}
        </Alert>
      )}

      {caveatLines.length > 0 && (
        <Box sx={{ px: 2, py: 1.25, bgcolor: '#FFFBEB', borderBottom: '1px solid rgba(11,11,15,0.08)' }}>
          <Typography variant="caption" sx={{ fontWeight: 700, display: 'block', mb: 0.25 }}>
            {t('bpDash.caveatsTitle')}
          </Typography>
          {caveatLines.map((line) => (
            <Typography key={line} variant="caption" color="text.secondary" sx={{ display: 'block' }}>
              · {line}
            </Typography>
          ))}
        </Box>
      )}

      {columns.length === 0 ? (
        <Box sx={{ p: 4, textAlign: 'center' }}>
          {/* No columns is not « zéro » — it is « rien n'a été mesuré ici ». And on a
              failed load it is not even that, so say nothing beyond the error. */}
          {!loadError && (
            <>
              <Typography variant="body2" color="text.secondary">
                {loading
                  ? t('bpMatrix.loading')
                  : detailExpired
                    ? t('bpDash.detailExpiredTitle')
                    : t('bpMatrix.empty')}
              </Typography>
              {!loading && (
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                  {/* Past the 90-day daily tier the grid has no columns for a reason
                      that is not about this brand's prompts: `BPPROMPT` rows have
                      expired and `BPPROMPTM#` has no writer yet. Saying « rien n'a
                      été mesuré » there would be false. */}
                  {detailExpired ? t('bpDash.detailExpired') : t('bpMatrix.emptyHint')}
                </Typography>
              )}
            </>
          )}
        </Box>
      ) : (
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ width: '38%', bgcolor: '#FAFBFC' }}>{t('axisPage.matrix.promptHeader')}</TableCell>
                {columns.map((col) => {
                  const isLatest = col.day === lastColumnDay;
                  return (
                    <TableCell
                      key={col.day}
                      align="center"
                      sx={{
                        bgcolor: isLatest ? `${latestAuditMeta.color}14` : '#FAFBFC',
                        fontSize: '0.72rem',
                        color: isLatest ? latestAuditMeta.color : 'text.secondary',
                        textTransform: 'uppercase',
                        fontWeight: 700,
                        minWidth: 80,
                        position: 'relative',
                      }}
                    >
                      <Box sx={{ textAlign: 'center' }}>
                        <Box sx={{ fontSize: '0.78rem' }}>
                          {/* 00b DQ9: a month column reads the month (« oct. 26 »), keyed on `col.month`. */}
                          {col.month ? columnLabel(col, i18n.language) : bucketLabel(col.day)}
                          {col.partial && (
                            <Tooltip title={t('bpMatrix.partialTooltip')}>
                              <Box component="span" sx={{ color: '#B45309', ml: 0.25 }}>
                                *
                              </Box>
                            </Tooltip>
                          )}
                        </Box>
                        <Box sx={{ fontSize: '0.65rem', fontWeight: 400, mt: 0.25 }}>
                          {grain === 'month' || col.month
                            ? t('bpMatrix.aggregated')
                            : isLatest
                              ? t('axisPage.matrix.latest')
                              : ''}
                        </Box>
                      </Box>
                    </TableCell>
                  );
                })}
              </TableRow>
            </TableHead>
            <TableBody>
              {visibleRows.length === 0 && (
                <TableRow>
                  <TableCell colSpan={columns.length + 1} sx={{ textAlign: 'center', py: 3, color: 'text.secondary' }}>
                    {t('axisPage.matrix.noMatch')}
                  </TableCell>
                </TableRow>
              )}
              {groups.map((group) => (
                <Fragment key={`g-${group.key}`}>
                  {group.label && (
                    <TableRow>
                      <TableCell
                        colSpan={columns.length + 1}
                        sx={{
                          bgcolor: '#F3F4F6',
                          fontSize: '0.7rem',
                          fontWeight: 700,
                          textTransform: 'uppercase',
                          color: 'text.secondary',
                          py: 0.75,
                        }}
                      >
                        {group.label} · {group.rows.length}
                      </TableCell>
                    </TableRow>
                  )}
                  {group.rows.map((row) => {
                    // E13: use row.key for all state lookups
                    const isOpen = expanded === row.key;
                    // Chips describe the most recent bucket that measured the prompt.
                    let queryEntry: BPPromptFact | null = null;
                    for (let i = row.cells.length - 1; i >= 0; i -= 1) {
                      if (row.cells[i]) { queryEntry = row.cells[i]; break; }
                    }
                    const selected = selectedCols.get(row.key);
                    const breaks = kindBreaks(row.cells);
                    return (
                      <Fragment key={`${group.key}-${row.key}`}>
                        <TableRow
                          hover
                          sx={{ cursor: 'pointer', bgcolor: isOpen ? `${latestAuditMeta.color}08` : undefined }}
                          onClick={() => handleExpand(row)}
                        >
                          <TableCell sx={{ verticalAlign: 'top' }}>
                            <Typography
                              variant="body2"
                              fontWeight={500}
                              sx={{
                                textDecoration: row.archived && !row.notConfigured ? 'line-through' : 'none',
                                color: row.archived ? 'text.secondary' : 'text.primary',
                              }}
                            >
                              {row.text}
                            </Typography>
                            <Box sx={{ display: 'flex', gap: 0.5, mt: 0.5, alignItems: 'center', flexWrap: 'wrap' }}>
                              {row.unknownPrompt ? (
                                <Tooltip title={t('bpMatrix.deletedPromptTooltip')}>
                                  <Chip
                                    label={t('bpMatrix.deletedPrompt')}
                                    size="small"
                                    sx={{ height: 18, fontSize: '0.65rem', fontWeight: 700, bgcolor: '#FEE2E2', color: '#991B1B' }}
                                  />
                                </Tooltip>
                              ) : row.notConfigured ? (
                                <Tooltip title={t('bpMatrix.notConfiguredTooltip')}>
                                  <Chip
                                    label={t('bpMatrix.notConfigured')}
                                    size="small"
                                    sx={{ height: 18, fontSize: '0.65rem', fontWeight: 700, bgcolor: '#F3F4F6', color: '#6B7280' }}
                                  />
                                </Tooltip>
                              ) : row.archived ? (
                                <Chip
                                  label={t('axisPage.matrix.archived')}
                                  size="small"
                                  sx={{ height: 18, fontSize: '0.65rem', fontWeight: 700, bgcolor: '#F3F4F6', color: '#6B7280' }}
                                />
                              ) : null}
                              {row.canReactivate && row.queryId && (
                                <Button
                                  size="small"
                                  variant="text"
                                  disabled={reactivating === row.key}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    const queryId = row.queryId;
                                    if (!queryId) return;
                                    setReactivating(row.key);
                                    onReactivate(queryId).finally(() => setReactivating(null));
                                  }}
                                  sx={{
                                    minWidth: 0,
                                    py: 0,
                                    px: 0.75,
                                    height: 18,
                                    fontSize: '0.65rem',
                                    fontWeight: 700,
                                    textTransform: 'none',
                                  }}
                                >
                                  {reactivating === row.key ? '…' : t('axisPage.matrix.reactivate')}
                                </Button>
                              )}
                              {!row.archived && row.queryId && (
                                <Button
                                  size="small"
                                  variant="text"
                                  color="inherit"
                                  disabled={reactivating === row.key}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    if (!row.queryId) return;
                                    setReactivating(row.key);
                                    onArchive(row.queryId).finally(() => setReactivating(null));
                                  }}
                                  sx={{
                                    minWidth: 0,
                                    py: 0,
                                    px: 0.75,
                                    height: 18,
                                    fontSize: '0.65rem',
                                    color: '#9CA3AF',
                                    textTransform: 'none',
                                    '&:hover': { color: '#EF4444', bgcolor: 'rgba(239,68,68,0.06)' },
                                  }}
                                >
                                  {reactivating === row.key ? '…' : t('axisPage.matrix.archive')}
                                </Button>
                              )}
                              {row.level && (
                                <Chip label={row.level} size="small" sx={{ height: 18, fontSize: '0.65rem' }} />
                              )}
                              {row.language && (
                                <Chip
                                  label={row.language.toUpperCase()}
                                  size="small"
                                  variant="outlined"
                                  sx={{ height: 18, fontSize: '0.65rem' }}
                                />
                              )}
                              {row.topic && (
                                <Chip
                                  label={row.topic}
                                  size="small"
                                  variant="outlined"
                                  sx={{
                                    height: 18,
                                    fontSize: '0.65rem',
                                    borderColor: `${latestAuditMeta.color}55`,
                                    color: latestAuditMeta.color,
                                  }}
                                />
                              )}
                              {row.zone && (
                                <Typography variant="caption" color="text.secondary">
                                  · {row.zone}
                                </Typography>
                              )}
                              {/* Best position across engines (referral only). Top
                                  spot gets a vivid chip; 2-3 a softer one; 4+ a
                                  muted one — the color tracks the score bands. */}
                              {axis === 'referral' && queryEntry?.bp != null && (
                                <Chip
                                  label={`#${queryEntry.bp}`}
                                  size="small"
                                  sx={{
                                    height: 18,
                                    fontSize: '0.65rem',
                                    fontWeight: 700,
                                    bgcolor:
                                      queryEntry.bp === 1 ? '#DCFCE7' : queryEntry.bp <= 3 ? '#FEF3C7' : '#F3F4F6',
                                    color:
                                      queryEntry.bp === 1 ? '#15803D' : queryEntry.bp <= 3 ? '#92400E' : '#6B7280',
                                  }}
                                />
                              )}
                              {queryEntry?.a && (
                                <Typography
                                  variant="caption"
                                  sx={{ color: ALIGNMENT_COLORS[queryEntry.a] || '#888' }}
                                >
                                  ● {ALIGNMENT_LABEL_KEYS[queryEntry.a] ? t(ALIGNMENT_LABEL_KEYS[queryEntry.a]) : queryEntry.a}
                                </Typography>
                              )}
                            </Box>
                          </TableCell>
                          {columns.map((col, idx) => {
                            const cell = row.cells[idx] ?? null;
                            if (!cell) {
                              // No row for this bucket = not measured. Never a 0.
                              return (
                                <TableCell key={col.day} align="center" sx={{ color: '#D1D5DB' }}>
                                  —
                                </TableCell>
                              );
                            }
                            const isSelected = selected?.has(col.day) ?? false;
                            // F step 8d: a fact without `s` was not evaluated — an empty
                            // dashed cell, never a number nor a score colour.
                            const scored = isScored(cell);
                            const colors = scoreCellColor(scored ? cell.s : null);
                            const isLatest = col.day === lastColumnDay;
                            const rate = (v?: number) =>
                              v == null ? t('bpDash.notMeasuredShort') : `${Math.round(v)} %`;
                            const baseTitle = !scored
                              ? t('axisPage.engine.judgeFailed')
                              : col.scoreOnly
                                ? t('bpMatrix.scoreOnlyTooltip')
                                : `${t('bpMatrix.cellCitation')} ${rate(cell.c)} · ${t('bpMatrix.cellReco')} ${rate(cell.r)}`;
                            return (
                              <TableCell
                                key={col.day}
                                align="center"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  // Score-only (detail expired): shown, never opened.
                                  if (col.scoreOnly) return;
                                  if (columns.length > 1) void toggleColumn(row, col.day);
                                  else handleExpand(row);
                                }}
                                sx={{
                                  cursor: col.scoreOnly ? 'default' : 'pointer',
                                  opacity: col.scoreOnly ? 0.75 : 1,
                                  transition: 'background-color 0.18s ease',
                                  bgcolor: isSelected ? `${latestAuditMeta.color}22` : 'transparent',
                                  // UI-SPEC §2.31: the first cell of a new series (criteria or target changed).
                                  ...(breaks[idx] ? { borderLeft: '2px solid #A1A1A1' } : {}),
                                }}
                              >
                                <Tooltip
                                  title={
                                    breaks[idx] ? (
                                      <>
                                        {baseTitle}
                                        <br />
                                        {t('axisPage.matrix.kindChange')}
                                      </>
                                    ) : (
                                      baseTitle
                                    )
                                  }
                                >
                                  <Box
                                    sx={{
                                      display: 'inline-flex',
                                      alignItems: 'center',
                                      justifyContent: 'center',
                                      minWidth: 44,
                                      height: 28,
                                      px: 1,
                                      borderRadius: 1,
                                      bgcolor: colors.bg,
                                      color: colors.fg,
                                      ...(scored ? {} : { border: '1px dashed #D1D5DB' }),
                                      fontWeight: 700,
                                      fontSize: '0.85rem',
                                      boxShadow: isSelected
                                        ? `0 0 0 4px ${latestAuditMeta.color}, 0 4px 12px ${latestAuditMeta.color}55`
                                        : isLatest
                                          ? `0 0 0 2px ${latestAuditMeta.color}`
                                          : 'none',
                                      transition: 'box-shadow 0.18s ease',
                                    }}
                                  >
                                    {scored ? cell.s : null}
                                    {col.scoreOnly && (
                                      <InfoOutlinedIcon sx={{ fontSize: 12, ml: 0.375, opacity: 0.7 }} />
                                    )}
                                  </Box>
                                </Tooltip>
                              </TableCell>
                            );
                          })}
                        </TableRow>
                        {isOpen && (
                          <TableRow>
                            <TableCell colSpan={columns.length + 1} sx={{ p: 0, bgcolor: `${latestAuditMeta.color}06` }}>
                              <Box sx={{ p: 2 }}>
                                {/* Authority-only quick actions for an under-performing
                                    prompt: seed the Studio with a content topic or open
                                    the Brand Knowledge + GEO strategy suggestion diff. */}
                                {axis === 'authority' &&
                                  !row.archived &&
                                  isScored(queryEntry) &&
                                  queryEntry.s < LOW_SCORE_THRESHOLD && (
                                    <AuthorityPromptActions
                                      brandId={brandId}
                                      auditId={latestAudit.auditId}
                                      promptText={row.text}
                                      contentCreated={contentCreated.has(contentActionId(row.queryTextHash))}
                                      onContentCreated={() => markContentCreated(row.queryTextHash)}
                                    />
                                  )}
                                {(() => {
                                  const days = Array.from(selected || []).sort();
                                  if (days.length === 0) {
                                    return (
                                      <Typography variant="caption" color="text.secondary">
                                        {t('axisPage.responses.title')}
                                      </Typography>
                                    );
                                  }
                                  const comparing = days.length === 2;
                                  return (
                                    <>
                                      {comparing && (grain === 'month' || days.some((d) => columns.find((c) => c.day === d)?.month)) && (
                                        <Typography
                                          variant="caption"
                                          color="text.secondary"
                                          sx={{ display: 'block', mb: 1, fontWeight: 600 }}
                                        >
                                          {t('bpMatrix.compareMonthlyNote')}
                                        </Typography>
                                      )}
                                      <Box
                                        sx={{
                                          display: 'grid',
                                          gridTemplateColumns: comparing ? '1fr 1fr' : '1fr',
                                          gap: 2,
                                        }}
                                      >
                                        {days.map((day) => {
                                          const colIdx = columns.findIndex((c) => c.day === day);
                                          const col = columns[colIdx];
                                          // F step 8d: the cell's own `aid` / `ae` (a month column
                                          // has no `auditId`); `ae` reads each engine's row from
                                          // its own audit. Legacy facts → the column's audit.
                                          const sources = cellSources(col, row.cells[colIdx]);
                                          const auditId = sources[0]?.auditId;
                                          // E14: filter by queryId membership in row.qids
                                          const panelRows = sources.flatMap((src) =>
                                            (resultsByAudit[src.auditId] || []).filter(
                                              (r) =>
                                                (!src.engine || engineKey(r.engine) === engineKey(src.engine)) &&
                                                (r.queryId
                                                  ? row.qids.includes(r.queryId)
                                                  : (r.queryTextHash || '') === row.queryTextHash),
                                            ),
                                          );
                                          return (
                                            <Box key={day}>
                                              <Typography
                                                variant="caption"
                                                color="text.secondary"
                                                sx={{ display: 'block', mb: 1, fontWeight: 600 }}
                                              >
                                                {sourceLabel(day, auditId, col?.month)}
                                              </Typography>
                                              {sources.some((src) => loadingAudit === src.auditId) ? (
                                                <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
                                                  <CircularProgress size={24} />
                                                </Box>
                                              ) : (
                                                <ExpandedQueryPanel
                                                  rows={panelRows}
                                                  brand={brand}
                                                  // E13: use row.key for engine state map
                                                  selectedEngine={selectedEngineByQuery.get(row.key) ?? null}
                                                  onEngineChange={(eng) =>
                                                    setSelectedEngineByQuery((prev) => {
                                                      const next = new Map(prev);
                                                      next.set(row.key, eng);
                                                      return next;
                                                    })
                                                  }
                                                />
                                              )}
                                            </Box>
                                          );
                                        })}
                                      </Box>
                                    </>
                                  );
                                })()}
                              </Box>
                            </TableCell>
                          </TableRow>
                        )}
                      </Fragment>
                    );
                  })}
                </Fragment>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Card>
  );
}

function ExpandedQueryPanel({
  rows,
  brand,
  selectedEngine,
  onEngineChange,
}: {
  rows: any[];
  brand: any;
  selectedEngine?: EngineKey | null;
  onEngineChange?: (engine: EngineKey) => void;
}) {
  const { t } = useTranslation();
  // Use external selectedEngine if provided (for sync across panels), otherwise determine default
  const currentEngine = selectedEngine ?? null;

  // Left-to-right tab order. AI Overview leads (it always searches, so it always
  // has citations); Mistral, the other secondary engine, stays last.
  const ENGINE_ORDER: EngineKey[] = [
    'ai_overview',
    'google_ai_mode',
    'perplexity',
    'openai',
    'gemini',
    'claude',
    'mistral',
  ];
  const byEngine = new Map<EngineKey, any>();
  for (const r of rows) {
    byEngine.set(engineKey(r.engine), r);
  }
  const orderedEngines = ENGINE_ORDER.filter((k) => byEngine.has(k));
  // Default to the first engine that actually returned sources — OpenAI/ChatGPT
  // never returns citations by design, so on tiers without Perplexity (e.g.
  // basic = ChatGPT + Gemini) the tab would otherwise open on the one engine
  // that always looks empty. Falls back to the first available engine when none
  // has sources. User selection (currentEngine) always wins.
  const defaultEngine =
    orderedEngines.find((k) => (byEngine.get(k)?.sources?.length ?? 0) > 0) ??
    orderedEngines[0];
  const current = currentEngine ?? defaultEngine;

  if (rows.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary">
        {t('axisPage.noResponseForPrompt')}
      </Typography>
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap' }}>
        {orderedEngines.map((k) => {
          const r = byEngine.get(k)!;
          const m = ENGINE_META[k];
          const active = current === k;
          return (
            <Box
              key={k}
              onClick={() => onEngineChange?.(k)}
              sx={{
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                px: 1.5,
                py: 0.75,
                borderRadius: 1,
                border: `1.5px solid ${active ? m.color : 'rgba(11,11,15,0.10)'}`,
                bgcolor: active ? m.bg : 'transparent',
              }}
            >
              <EngineLogo engine={k} size={18} />
              <Typography variant="body2" fontWeight={600} sx={{ color: active ? m.color : 'text.primary' }}>
                {m.label}
              </Typography>
              <Chip
                label={typeof r.score === 'number' ? `${r.score}` : t('axisPage.engine.judgeFailed')}
                size="small"
                sx={{ height: 18, fontSize: '0.7rem', fontWeight: 700, bgcolor: 'rgba(11,11,15,0.04)' }}
              />
            </Box>
          );
        })}
      </Box>
      {current && byEngine.get(current) && (
        <EngineResponsePanel
          row={byEngine.get(current)}
          engine={current}
          brandId={brand?.brandId}
          brandName={brand?.name}
          brandUrl={brand?.url}
          aliases={brand?.strategy?.aliases}
          showSources={true}
          queryText={byEngine.get(current).query}
        />
      )}
    </Box>
  );
}

// ── Recommendations are rendered via the shared RecommendationList component
//    (see components/recommendations/). The former per-page RecommendationsBlock
//    + RecommendationCard were replaced by the unified layout.

/**
 * "Non cité ?" hint next to a row's Cité/Non cité chip. `is_brand_cited`
 * (scoring.py) only matches the FULL brand name or an explicit alias — a
 * multi-word brand referred to by a shorter name (e.g. "Vitalis" for
 * "Vitalis Poitiers") reads as not-cited until an alias is configured. This
 * lets the user add one inline, without leaving the page or re-validating
 * Brand Knowledge (`aliases` is excluded from the strategy staleness hash).
 */
/**
 * The row is named but steered away from: referral scores it 0 instead of the
 * cited-only 50. Mirrors the `sentiment === 'negative'` branch of
 * `scorer.compute_axis_score(axis='referral', ...)` — keep the two in lock-step,
 * this is the only thing that explains a 0 sitting next to a "Cité" chip.
 */
function isDisrecommended(row: any): boolean {
  return (
    (row.axis || '').toLowerCase() === 'referral' &&
    !!row.cited &&
    !row.recommended &&
    row.sentiment === 'negative'
  );
}

function NotCitedHint({ brandId, aliases }: { brandId?: string; aliases: string[] }) {
  const { t } = useTranslation();
  const toast = useToast();
  const { refreshBrands } = useBrand();
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [value, setValue] = useState('');
  const [saving, setSaving] = useState(false);
  const maxItems = BRAND_STRATEGY_LIMITS.aliases.maxItems;
  const maxChars = BRAND_STRATEGY_LIMITS.aliases.maxCharsPerItem;
  const atLimit = aliases.length >= maxItems;

  const close = () => {
    setAnchorEl(null);
    setValue('');
  };

  const handleAdd = async () => {
    const alias = value.trim();
    if (!alias || !brandId || saving || atLimit) return;
    setSaving(true);
    try {
      const next = [...aliases, alias];
      await api.patch(`/brands/${brandId}/strategy`, { aliases: next });
      await refreshBrands();
      toast.show(t('axisPage.engine.notCitedHint.success'));
      close();
    } catch {
      toast.show(t('axisPage.engine.notCitedHint.error'), { severity: 'error' });
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <IconButton
        size="small"
        onClick={(e) => setAnchorEl(e.currentTarget)}
        title={t('axisPage.engine.notCitedHint.tooltip')}
        sx={{ width: 20, height: 20, color: 'text.disabled' }}
      >
        <HelpOutlineIcon sx={{ fontSize: 16 }} />
      </IconButton>
      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={close}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        slotProps={{ paper: { sx: { maxWidth: 340, p: 2 } } }}
      >
        <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 0.5 }}>
          {t('axisPage.engine.notCitedHint.popoverTitle')}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
          {t('axisPage.engine.notCitedHint.popoverBody')}
        </Typography>
        {atLimit ? (
          <Alert severity="warning" sx={{ mb: 0 }}>
            {t('axisPage.engine.notCitedHint.limitReached', { max: maxItems })}
          </Alert>
        ) : (
          <Box sx={{ display: 'flex', gap: 1 }}>
            <TextField
              size="small"
              fullWidth
              autoFocus
              value={value}
              onChange={(e) => setValue(e.target.value.slice(0, maxChars))}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  handleAdd();
                }
              }}
              placeholder={t('axisPage.engine.notCitedHint.placeholder')}
              inputProps={{ maxLength: maxChars }}
              disabled={saving}
            />
            <Button
              variant="contained"
              size="small"
              onClick={handleAdd}
              disabled={saving || !value.trim()}
              startIcon={saving ? <CircularProgress size={14} color="inherit" /> : undefined}
            >
              {t('axisPage.engine.notCitedHint.add')}
            </Button>
          </Box>
        )}
      </Popover>
    </>
  );
}

function EngineResponsePanel({
  row,
  engine,
  brandId,
  brandName,
  brandUrl,
  aliases,
  showSources,
  showEnhance,
  onEnhance,
  enhancingKey,
  queryText,
}: {
  row: any;
  engine: EngineKey;
  brandId?: string;
  brandName?: string;
  brandUrl?: string;
  aliases?: string[];
  showSources?: boolean;
  showEnhance?: boolean;
  onEnhance?: (query: string, response: string) => void;
  enhancingKey?: string | null;
  queryText?: string;
}) {
  const { t } = useTranslation();
  const meta = ENGINE_META[engine];
  // Same fetch as HighlightedResponse — the promise is cached per brand, so this
  // costs a hook, not a second request.
  const { colors: marqueColors, domainHexes } = useMarqueColors(brandId);
  const sourceColors = useMemo(
    () => sourceColorsFrom(row.targetSourceHighlights, marqueColors),
    [row.targetSourceHighlights, marqueColors],
  );
  const enhanceQuery = queryText || row.query;
  const enhanceLoading = !!enhanceQuery && enhancingKey === enhanceQuery;
  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1, flexWrap: 'wrap' }}>
        <EngineLogo engine={engine} size={20} />
        <Typography variant="subtitle2" fontWeight={700} sx={{ color: meta.color }}>
          {meta.label}
        </Typography>
        <Chip
          label={row.cited ? t('axisPage.engine.cited') : t('axisPage.engine.notCited')}
          size="small"
          color={row.cited ? 'success' : 'error'}
          variant="outlined"
          sx={{ height: 20 }}
        />
        {!row.cited && <NotCitedHint brandId={brandId} aliases={aliases || []} />}
        {/*
          `judgeFailed` ⇒ `recommended` / `sentiment` / `narrativeAlignment` are
          neutral DEFAULTS, not judgements (see `judgeFailed` in audit.ts). Showing
          them would render "Non recommandé · neutral" on an answer that may well
          recommend the brand — so show one honest chip instead. `cited` and the
          target chips are deterministic and stay.
        */}
        {row.judgeFailed ? (
          <Chip
            label={t('axisPage.engine.judgeFailed')}
            title={t('axisPage.engine.judgeFailedTooltip')}
            size="small"
            variant="outlined"
            sx={{ height: 20, borderStyle: 'dashed', color: 'text.disabled', fontStyle: 'italic' }}
          />
        ) : (
          <>
            <Chip
              label={
                row.recommended
                  ? row.recommendedPosition && row.recommendedTotal
                    ? t('axisPage.engine.recommendedPositionTotal', { position: row.recommendedPosition, total: row.recommendedTotal })
                    : row.recommendedPosition
                      ? t('axisPage.engine.recommendedPosition', { position: row.recommendedPosition })
                      : t('axisPage.engine.recommended')
                  : t('axisPage.engine.notRecommended')
              }
              // Cited + negative + not recommended is the engine steering the
              // buyer AWAY from the brand ("À éviter pour ton exigence de délai"),
              // which scores 0 rather than the cited-only 50. Say why, or a 0 next
              // to "Cité" reads as a bug.
              title={isDisrecommended(row) ? t('axisPage.engine.disrecommendedTooltip') : undefined}
              size="small"
              color={row.recommended ? 'success' : isDisrecommended(row) ? 'error' : 'default'}
              variant="outlined"
              sx={{ height: 20 }}
            />
            <Chip
              label={row.sentiment}
              size="small"
              sx={{
                height: 20,
                bgcolor:
                  row.sentiment === 'positive'
                    ? '#00DDB320'
                    : row.sentiment === 'negative'
                      ? '#FF444420'
                      : '#8B9DC320',
                color:
                  row.sentiment === 'positive'
                    ? '#00DDB3'
                    : row.sentiment === 'negative'
                      ? '#FF4444'
                      : '#8B9DC3',
                fontWeight: 600,
              }}
            />
            {row.narrativeAlignment && (
              <Chip
                label={t('axisPage.engine.alignmentLabel', { value: ALIGNMENT_LABEL_KEYS[row.narrativeAlignment] ? t(ALIGNMENT_LABEL_KEYS[row.narrativeAlignment]) : row.narrativeAlignment })}
                size="small"
                sx={{
                  height: 20,
                  bgcolor: `${ALIGNMENT_COLORS[row.narrativeAlignment] || '#888'}20`,
                  color: ALIGNMENT_COLORS[row.narrativeAlignment] || '#888',
                  fontWeight: 600,
                }}
              />
            )}
          </>
        )}
        {(row.targetCategories || []).map(({ category, count }: { category: string; count: number }) => {
          const cc = CATEGORY_CHIP_COLOR[category];
          if (!cc) return null;
          return (
            <Chip
              key={category}
              label={count > 1
                ? `${t(`cibles.tab.${category}`)} ×${count}`
                : t(`cibles.tab.${category}`)}
              size="small"
              sx={{ height: 20, bgcolor: cc.bg, color: cc.fg, fontWeight: 600 }}
            />
          );
        })}
      </Box>

      {row.alignmentReason &&
        (row.narrativeAlignment === 'weak' ||
          row.narrativeAlignment === 'misaligned' ||
          row.narrativeAlignment === 'partial') && (
          <Box
            sx={{
              display: 'flex',
              gap: 1,
              mb: 1,
              p: 1,
              borderRadius: 1,
              bgcolor: `${ALIGNMENT_COLORS[row.narrativeAlignment] || '#888'}12`,
            }}
          >
            <InfoOutlinedIcon
              sx={{ fontSize: 16, mt: '2px', color: ALIGNMENT_COLORS[row.narrativeAlignment] || '#888' }}
            />
            <Typography variant="caption" sx={{ color: 'text.secondary', lineHeight: 1.5 }}>
              {row.alignmentReason}
            </Typography>
          </Box>
        )}

      {row.geoFallback?.reason === 'location_unsupported' && (
        // The answer is a real measurement, only not localized: said on the answer itself.
        <Typography variant="caption" component="div" sx={{ color: 'text.secondary', mb: 1 }}>
          {t('bpMatrix.geoFallbackLocation', { engine: meta.label, country: row.geoFallback.country })}
        </Typography>
      )}

      <Box
        sx={{
          bgcolor: 'rgba(11,11,15,0.03)',
          borderLeft: `3px solid ${meta.color}`,
          borderRadius: 1,
          p: 2,
        }}
      >
        <HighlightedResponse
          text={row.response || ''}
          brandId={brandId}
          sources={row.sources || []}
          sourceLinks={row.sourceLinks || []}
          targetHighlights={row.targetHighlights}
          targetSourceHighlights={row.targetSourceHighlights}
        />
      </Box>

      {showSources && row.sources && row.sources.length > 0 && (
        <Box sx={{ mt: 2 }}>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
            {t('axisPage.engine.sources', { count: row.sources.length })}
            {row.brandIsSource && (
              <Chip label={t('axisPage.engine.yourSiteIsSource')} size="small" color="success" sx={{ ml: 1 }} />
            )}
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
            {row.sources.map((url: string, si: number) => {
              const isBrand = isBrandUrl(url, brandName, brandUrl);
              const domain = extractDomain(url);
              // Chip colour priority (D45):
              //   1. Registered Cibles target matched this source → target's marque colour.
              //      Also covers brand-domain URLs that are registered targets (e.g.
              //      `salon.dessange.com` as a marque domain target → DESSANGE blue, not green).
              //   2. Brand domain but no registered target → look up the marque whose
              //      `domain` field owns this host and use its configured colour.  This
              //      covers every unregistered page on the brand's domain(s) — a blog post,
              //      a /catalogue page — so the chip is consistent with the target chips.
              //      Falls back to the generic green only when no marque has a domain that
              //      matches (brand has no marques configured, or the domain differs).
              //   3. Neither → default purple outline (third-party source, no target).
              const targetHex = sourceColors.get(si);
              // Brand-domain chips with no registered target: look up the marque whose
              // domain field matches, so the chip uses the configured brand colour.
              // Subdomain and sibling-TLD matches are handled by the isBrandOwnedHost loop.
              const brandDomainHex = (!targetHex && isBrand && brandUrl)
                ? (() => {
                    const bh = bareHost(brandUrl);
                    if (domainHexes[bh]) return domainHexes[bh];
                    for (const [d, hex] of Object.entries(domainHexes)) {
                      if (isBrandOwnedHost(bh, d) || isBrandOwnedHost(d, bh)) return hex;
                    }
                    return undefined;
                  })()
                : undefined;
              const activeHex = targetHex ?? brandDomainHex;
              const href = sourceHref(row.sourceLinks?.[si] || url);
              return (
                <Chip
                  key={si}
                  label={domain.slice(0, 50)}
                  size="small"
                  variant={activeHex || isBrand ? 'filled' : 'outlined'}
                  // Never surface the opaque wrapper URL as a tooltip.
                  title={href && !isGroundingWrapper(href) ? href : domain}
                  clickable={!!href}
                  {...(href
                    ? { component: 'a', href, target: '_blank', rel: 'noopener noreferrer' }
                    : {})}
                  sx={
                    activeHex
                      ? { bgcolor: `${activeHex}26`, color: activeHex, fontWeight: 700 }
                      : isBrand
                        ? { bgcolor: 'rgba(16, 185, 129, 0.12)', color: '#10B981', fontWeight: 700 }
                        : { bgcolor: 'rgba(124, 58, 237, 0.10)', borderColor: 'rgba(124, 58, 237, 0.32)', color: '#7C3AED' }
                  }
                />
              );
            })}
          </Box>
        </Box>
      )}

      {showEnhance && onEnhance && (
        <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
          <Button
            size="small"
            variant="outlined"
            startIcon={
              enhanceLoading ? (
                <CircularProgress size={14} sx={{ color: 'primary.main' }} />
              ) : (
                <TrendingUpIcon sx={{ color: 'primary.main' }} />
              )
            }
            disabled={enhanceLoading || !enhanceQuery}
            onClick={() => onEnhance(enhanceQuery, row.response || '')}
            sx={{
              borderColor: 'rgba(124, 58, 237, 0.5)',
              color: 'primary.main',
              '&:hover': { borderColor: 'primary.main', bgcolor: 'rgba(124, 58, 237, 0.08)' },
            }}
          >
            {enhanceLoading ? t('axisPage.engine.analyzing') : t('axisPage.engine.appearMore')}
          </Button>
        </Box>
      )}
    </Box>
  );
}
